import { useState } from 'react';
import { ItemArt } from '../avatar/itemArt';
import { type CatalogItem, type DistanceUnit, type HuntView, type PartHint, compass, isVeryClose, itemPart, roughDistance, toggleUnit, uncertaintyLabel } from '../models/types';
import { colors, rarityColors, withAlpha } from '../theme';
import { Bubble } from './Bubble';
import { RarityBadge } from './RarityBadge';
import { TimeLeftLabel } from './TimeLeftLabel';

/**
 * An active part hunt: time left, progress, collected part chips, and a compass hint per
 * missing part. `now` is the device clock in unix seconds.
 */
export function HuntBubble({
  hunt,
  item,
  now,
  onRefreshLocations,
  refreshing = false,
  unit = 'meters',
  onToggleUnit,
}: {
  hunt: HuntView;
  item: CatalogItem;
  now: number;
  /** Re-measure the hints from the player's current position, without a scan. Hidden when absent. */
  onRefreshLocations?: () => void;
  refreshing?: boolean;
  /** The unit hint distances are shown in. */
  unit?: DistanceUnit;
  /** Tapping a hint's distance switches the unit. Distances are plain text when absent. */
  onToggleUnit?: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const color = rarityColors[hunt.rarity];
  const progress = hunt.totalParts === 0 ? 0 : hunt.collected.length / hunt.totalParts;

  return (
    <Bubble border={`3px solid ${withAlpha(color, 0.5)}`} onClick={() => setExpanded((e) => !e)} containsControls testId={`hunt-${hunt.itemId}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <ItemArt item={item} style={{ fontSize: 34 }} />
        <div style={{ flex: 1 }}>
          <div className="t-title-m">{item.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <RarityBadge rarity={hunt.rarity} compact />
            <span className="t-label">{hunt.collected.length}/{hunt.totalParts} parts</span>
          </div>
          {hunt.expiresAt != null && (
            <div style={{ marginTop: 4 }}>
              <TimeLeftLabel expiresAt={hunt.expiresAt} now={now} />
            </div>
          )}
        </div>
        <span aria-hidden="true" style={{ opacity: 0.5 }}>{expanded ? '▴' : '▾'}</span>
      </div>
      <div className="progress" style={{ background: withAlpha(color, 0.15), marginTop: 12 }} role="progressbar" aria-valuenow={hunt.collected.length} aria-valuemax={hunt.totalParts}>
        <div style={{ width: `${progress * 100}%`, background: color }} />
      </div>
      {expanded && (
        <>
          <div className="chips" style={{ marginTop: 14 }}>
            {item.parts.map((p) => {
              const got = hunt.collected.includes(p.id);
              return (
                <span key={p.id} className="chip" style={{ background: got ? colors.lime : withAlpha(colors.sky, 0.25) }}>
                  <span>{got ? '✅' : '❔'}</span>
                  {p.name}
                </span>
              );
            })}
          </div>
          {hunt.hints.length > 0 && (
            <div style={{ marginTop: 14 }}>
              {hunt.hints.map((h) => (
                <HintRow key={h.partId} hint={h} name={itemPart(item, h.partId)?.name ?? h.partId} color={color} unit={unit} onToggleUnit={onToggleUnit} />
              ))}
              {onRefreshLocations && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn text"
                    disabled={refreshing}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRefreshLocations();
                    }}
                    data-testid="refresh-locations"
                  >
                    {refreshing ? 'Refreshing…' : '📍 Refresh locations'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Bubble>
  );
}

function HintRow({ hint, name, color, unit, onToggleUnit }: { hint: PartHint; name: string; color: string; unit: DistanceUnit; onToggleUnit?: () => void }) {
  const close = isVeryClose(hint);
  const margin = uncertaintyLabel(hint, unit);
  const distance = (
    <>
      <span className="t-title-s" style={{ color, display: 'block' }}>
        {close ? 'Very close' : `${roughDistance(hint, unit)} ${compass(hint)}`}
      </span>
      {margin && <span className="t-small" style={{ opacity: 0.6, display: 'block' }}>{close ? 'look around' : margin}</span>}
    </>
  );
  return (
    <div className="hint-row">
      <div className="hint-arrow" style={{ background: withAlpha(color, 0.15), color }}>
        {close ? (
          <span aria-hidden="true">📍</span>
        ) : (
          <span style={{ display: 'inline-block', transform: `rotate(${hint.bearingDegrees}deg)` }} aria-hidden="true">⬆</span>
        )}
      </div>
      <span className="t-title-s" style={{ flex: 1 }}>{name}</span>
      {close || !onToggleUnit ? (
        <span style={{ textAlign: 'right' }}>{distance}</span>
      ) : (
        // Tapping a distance switches every hint between meters and feet.
        <button
          type="button"
          className="distance-toggle"
          data-testid="distance-toggle"
          aria-label={`Distance in ${unit}, tap to switch to ${toggleUnit(unit)}: ${roughDistance(hint, unit)} ${compass(hint)}${margin ? `, ${margin}` : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleUnit();
          }}
        >
          {distance}
        </button>
      )}
    </div>
  );
}
